import type SenderSource from '@/data/models/domain/SenderSource'
import type PhoneNumber from '@/data/models/domain/PhoneNumber'
import type { ModelRaw, ValueOf } from '@/types'
import type WhatsAppBusinessPhone from '@/data/models/domain/whatsApp/WhatsAppBusinessPhone'
import type WebWidget from '@/data/models/domain/messages/settings/webWidget/webWidget'
import type FacebookPage from '@/data/models/domain/messenger/facebook/FacebookPage'
import type InstagramAccount from '@/data/models/domain/messenger/instagram/InstagramAccount'
import type { ParsedUserNumbers, UserNumbersKeys } from '@/services/domain/user/types'
import type SenderSettingsCountry from '@/data/models/domain/SenderSettingsCountry'
import { type ChannelNumberType, isChannelNumberType } from '@/services/domain/senderSettings/senderChannelTypes'
import type { selectOptionLabelKey, selectOptionValueKey, selectSelectedOptionLabelKey } from '@/services/forms/types'
import type { TmCountryCode } from '@/services/types'

export type FieldSelectSearchSenderDataResult = ParsedUserNumbers & {
  whatsApp: WhatsAppBusinessPhone[]
  widgets: WebWidget[]
  facebook: FacebookPage[]
  instagram: InstagramAccount[]
}

export interface IAddOptionsSection {
  dedicated: ModelRaw<SenderSource> | ModelRaw<PhoneNumber>
  senderIds: ModelRaw<SenderSource>
  userNumbers: ModelRaw<SenderSource>
  shared: ModelRaw<SenderSource>
  byocNumbers: ModelRaw<SenderSource>
  whatsApp: WhatsAppBusinessPhone
  widgets: WebWidget
  facebook: FacebookPage
  instagram: InstagramAccount
  twilio: ModelRaw<SenderSource>
  vonage: ModelRaw<SenderSource>
  bandwidth: ModelRaw<SenderSource>
  sinch: ModelRaw<SenderSource>
  infobip: ModelRaw<SenderSource>
  messagebird: ModelRaw<SenderSource>
  aws: ModelRaw<SenderSource>
  telnyx: ModelRaw<SenderSource>
}

export type AddOptionsSection = ValueOf<{
  [P in keyof IAddOptionsSection]: {
    title: string
    numberType: P
    senderSources: IAddOptionsSection[P][]
  }
}>

export type MapSenderSourcesToSubItemsParams = ValueOf<{
  [P in keyof IAddOptionsSection]: {
    numberType: P
    sender: IAddOptionsSection[P]
    senderSettingsCountry?: SenderSettingsCountry
  }
}>

export type ChannelMapSenderParams = ValueOf<{
  [P in ChannelNumberType]: {
    numberType: P
    sender: IAddOptionsSection[P]
    senderSettingsCountry?: SenderSettingsCountry
  }
}>

export const isChannelMapSenderParams = (params: MapSenderSourcesToSubItemsParams): params is ChannelMapSenderParams =>
  isChannelNumberType(params.numberType)

interface IDefaultOption {
  [selectOptionLabelKey]: string
  [selectOptionValueKey]: string
  [selectSelectedOptionLabelKey]?: string
}

export type FieldParams = {
  getDefaultOption?: () => IDefaultOption | null
  hideOverrideOptionsFor?: UserNumbersKeys[]
  hideEmptyOptionsFor?: UserNumbersKeys[]
  hideOptionsFor?: UserNumbersKeys[]
  hideTitleOptionsFor?: UserNumbersKeys[]
  enableByocSenderId?: boolean
  isOpenBuyTMDedicatedNumberInNewTab?: boolean
  senderSettings?: {
    countries: SenderSettingsCountry[]
    countryId: TmCountryCode
  }
}

export type GetCallsOptionsParams = Pick<FieldParams, 'hideEmptyOptionsFor' | 'hideTitleOptionsFor'>
